import { LegalPage } from "../../components/LegalPage";

export function PlatformAiBubbleMethodology() {
  return (
    <LegalPage
      title="Platform AI Assistant — methodology"
      eyebrow="Trust center"
      description="How the WeConsult Rx in-app AI assistant bubble protects PHI: tenant + user isolation, intent classification, audit retention, Bedrock BAA coverage."
      path="/platform-ai-bubble-methodology"
      effective="2026-05-31"
    >
      <p>
        The floating AI assistant bubble inside the WeConsult Rx app is an
        intent-routed Q&amp;A surface. It composes a question, classifies its
        intent, and forwards it to the relevant downstream surface (clinical
        Q&amp;A, regulatory reference library, in-app help, or universal
        search). This page documents the privacy and compliance guarantees
        that make that routing safe to use over Protected Health Information
        (PHI) systems.
      </p>

      <h2>Isolation guarantees</h2>
      <ul>
        <li>
          <strong>Tenant isolation.</strong> Every session row, message row,
          audit row, and config row carries a non-null <code>tenant_id</code>.
          Row Level Security (RLS) on Postgres scopes <em>all</em> reads to
          the caller&apos;s tenant via the <code>current_tenant_id()</code> SQL
          function. Cross-tenant reads are physically impossible via the
          authenticated path.
        </li>
        <li>
          <strong>User isolation.</strong> Sessions and messages are scoped
          to <code>user_id = auth.uid()</code>. One user&apos;s thread is
          never visible to another user, even within the same tenant.
        </li>
        <li>
          <strong>Per-tenant kill switch.</strong> Tenant owners can disable
          the bubble (<code>tenant_ai_bubble_config.enabled = false</code>),
          removing the FAB for every user in that tenant.
        </li>
      </ul>

      <h2>PHI protection</h2>
      <ul>
        <li>
          <strong>User message bodies are never stored.</strong> When a user
          sends a message, the server persists only a SHA-256 hash of the
          text. The plaintext leaves the request handler and is never
          written to disk. (See <code>platform_ai_messages</code> schema
          CHECK constraint <code>platform_ai_messages_phi_invariant</code>.)
        </li>
        <li>
          <strong>Assistant responses are stored in full.</strong> Per HIPAA
          §164.528 (accounting of disclosures), we must be able to reproduce
          what was disclosed to a user. Assistant outputs are model
          responses based on PHI-scrubbed prompts, so they do not contain
          unredacted user-provided PHI.
        </li>
        <li>
          <strong>PHI redaction strict mode.</strong> When enabled (default),
          a regex pre-scan on both client and server refuses to forward
          messages containing SSN-like, phone-like, DOB-like, or MRN-like
          patterns to the language model. The user is shown a reword
          suggestion and directed to the encrypted team-thread surface on
          the resident&apos;s page for PHI-bearing collaboration.
        </li>
        <li>
          <strong>Allergy-suppression block.</strong> The PHI 66 gate that
          governs S19 Clinical Assistant also applies here: any user message
          requesting suppression of allergy checks is rejected pre-LLM, and
          any model output containing the same pattern is scrubbed.
        </li>
      </ul>

      <h2>Intent classification &amp; routing</h2>
      <p>
        Each incoming message is passed to a low-cost Anthropic Claude Haiku
        invocation that returns exactly one word: <code>clinical</code>,
        <code>regulatory</code>, <code>help</code>, <code>search</code>,
        <code>quick-action</code>, or <code>unclear</code>. The classifier
        sees only the message text and the routing instructions — it never
        sees tenant context, resident data, or other PHI. Based on the
        classification, the message is forwarded:
      </p>
      <ul>
        <li><strong>clinical</strong> → S19 Clinical Assistant</li>
        <li><strong>regulatory</strong> → S45 Regulatory Reference Library</li>
        <li><strong>help</strong> → S47 In-App Help Center</li>
        <li><strong>search</strong> → S46 Universal Search</li>
        <li><strong>quick-action</strong> → confirm-and-execute UI (navigation, draft creation)</li>
        <li><strong>unclear</strong> → clarifying question</li>
      </ul>

      <h2>Bedrock BAA coverage</h2>
      <p>
        The classifier and S19 invocations both run via AWS Bedrock under
        our existing Business Associate Agreement with AWS. No prompts or
        responses leave AWS Bedrock&apos;s service boundary for training,
        analytics, or third-party use. See our{" "}
        <a href="/subprocessors">subprocessors list</a> for the full
        accounting.
      </p>

      <h2>Audit retention</h2>
      <ul>
        <li>
          Every session start, message routing, PHI block, budget overage,
          admin redaction, and config update writes a row to
          <code>audit_log</code>.
        </li>
        <li>
          Default audit retention is <strong>2,190 days (6 years)</strong>{" "}
          per HIPAA §164.316(b)(2). Tenants may extend the retention period
          but not shorten it below the regulatory floor.
        </li>
        <li>
          Audit metadata <em>never</em> contains user-message body text.
          Only intent, routed-to label, token counts, and the SHA-256 body
          hash are persisted.
        </li>
      </ul>

      <h2>Cost controls</h2>
      <ul>
        <li>
          Each tenant has a configurable <strong>daily token budget</strong>{" "}
          (default 100,000). When exceeded, additional requests return 429
          and an audit row is written.
        </li>
        <li>
          The intent classifier is a cheap Haiku invocation (router-only,
          not synthesis), keeping per-message overhead negligible.
        </li>
      </ul>

      <h2>Tenant admin controls</h2>
      <p>
        Tenant owners and admins manage the bubble at{" "}
        <code>/admin/ai-config</code> inside the app: kill switch, route
        toggles (clinical / regulatory / help / search), PHI strict-mode
        toggle, daily budget, audit retention, custom banner, and the
        emergency assistant-message redact tool.
      </p>

      <h2>Educational reference, not advice</h2>
      <p>
        The AI assistant bubble is an educational reference surface. It is
        not medical advice and is not legal advice. The consulting
        pharmacist or facility administrator remains the authoritative
        decision-maker for every recommendation, compliance question, and
        clinical action.
      </p>
    </LegalPage>
  );
}
